import React, { useState } from "react";
import {
  Avatar,
  AvatarSize,
  Iconography,
  IconographySizes,
  Loader,
  LoaderSizes,
} from "@hid-galaxy-ui/galaxy-react";
import { UserListType } from "../../../../../redux/slices/user/Types";
import UserStatusBadge from "./UserStatusBadge";

export interface UserListActions {
  onEdit: (user: UserListType[0]) => void;
  onDelete?: (user: UserListType[0]) => void;
  onInvite?: (user: UserListType[0]) => void;
  inviteLoading?: boolean;
}

interface UserManagementListProps {
  users: UserListType;
  actions: UserListActions;
  readOnly?: boolean;
  showPartnerColumn?: boolean;
  partnerNameById?: Record<number, string>;
}

const resolvePartnerName = (
  user: UserListType[0],
  partnerNameById?: Record<number, string>
): string => {
  if (user.partner_name) return user.partner_name;
  const partnerId = user.metadata?.partner;
  if (partnerId != null && partnerNameById?.[partnerId]) {
    return partnerNameById[partnerId];
  }
  return "—";
};

const UserManagementList: React.FC<UserManagementListProps> = ({
  users,
  actions,
  readOnly = false,
  showPartnerColumn = false,
  partnerNameById,
}) => {
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  }>({
    key: "",
    direction: "asc",
  });

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  if (users.length === 0) {
    return <div className="no-data">No users found</div>;
  }

  const sortedUsers = [...users].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue = "";
    let bValue = "";

    switch (sortConfig.key) {
      case "userName":
        aValue = `${a.first_name || ""} ${a.last_name || ""}`;
        bValue = `${b.first_name || ""} ${b.last_name || ""}`;
        break;

      case "email":
        aValue = a.email || "";
        bValue = b.email || "";
        break;

      case "role":
        aValue = a.role || "";
        bValue = b.role || "";
        break;

      case "status":
        aValue = a.status || "";
        bValue = b.status || "";
        break;

      case "partnerName":
        aValue = resolvePartnerName(a, partnerNameById);
        bValue = resolvePartnerName(b, partnerNameById);
        break;

      default:
        return 0;
    }

    return sortConfig.direction === "asc"
      ? aValue.localeCompare(bValue)
      : bValue.localeCompare(aValue);
  });

  return (
    <div
      className={`table-container um-table${
        showPartnerColumn ? " um-table--with-partner" : ""
      }`}
    >
      <div className="table-row um-table__row table__head">
        <div
          className="table-col um-table__col um-table__col--user"
          onClick={() => handleSort("userName")}
          style={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          User name
          <Iconography
            icon={
              sortConfig.key === "userName"
                ? sortConfig.direction === "asc"
                  ? "angleUp"
                  : "angleDown"
                : "sort"
            }
            size={IconographySizes.Small}
          />
        </div>
        <div
          className="table-col um-table__col um-table__col--email"
          onClick={() => handleSort("email")}
          style={{ cursor: "pointer" }}
        >
          Partner email
          <Iconography
            icon={
              sortConfig.key === "email"
                ? sortConfig.direction === "asc"
                  ? "angleUp"
                  : "angleDown"
                : "sort"
            }
            size={IconographySizes.Small}
          />
        </div>
        {showPartnerColumn && (
          <div
            className="table-col um-table__col um-table__col--partner"
            onClick={() => handleSort("partnerName")}
            style={{ cursor: "pointer" }}
          >
            Partner Name
            <Iconography
              icon={
                sortConfig.key === "partner"
                  ? sortConfig.direction === "asc"
                    ? "angleUp"
                    : "angleDown"
                  : "sort"
              }
              size={IconographySizes.Small}
            />
          </div>
        )}
        <div
          className="table-col um-table__col um-table__col--role"
          onClick={() => handleSort("role")}
          style={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          Role
          <Iconography
            icon={
              sortConfig.key === "role"
                ? sortConfig.direction === "asc"
                  ? "angleUp"
                  : "angleDown"
                : "sort"
            }
            size={IconographySizes.Small}
          />
        </div>
        <div
          className="table-col um-table__col um-table__col--status"
          onClick={() => handleSort("status")}
          style={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          Status
          <Iconography
            icon={
              sortConfig.key === "status"
                ? sortConfig.direction === "asc"
                  ? "angleUp"
                  : "angleDown"
                : "sort"
            }
            size={IconographySizes.Small}
          />
        </div>
        <div className="table-col um-table__col um-table__col--actions">
          Action
        </div>
      </div>

      {sortedUsers.map((user, index) => (
        <div
          className="table-row um-table__row"
          key={user.ID}
          style={index === 0 ? { borderTop: "none" } : undefined}
        >
          <div className="table-col um-table__col um-table__col--user">
            <Avatar size={AvatarSize.Large} />
            <span className="um-table__name">
              {[user.first_name, user.last_name].filter(Boolean).join(" ") ||
                "—"}
            </span>
          </div>
          <div className="table-col um-table__col um-table__col--email">
            {user.email ?? "—"}
          </div>
          {showPartnerColumn && (
            <div className="table-col um-table__col um-table__col--partner">
              {resolvePartnerName(user, partnerNameById)}
            </div>
          )}
          <div className="table-col um-table__col um-table__col--role">
            {user.role}
          </div>
          <div className="table-col um-table__col um-table__col--status">
            <UserStatusBadge status={user.status} />
          </div>
          <div className="table-col um-table__col um-table__col--actions">
            <div className="um-table__actions-cell">
              <div className="um-table__invite-slot">
                {user.status === "Invited" && actions.onInvite && (
                  <div className="um-table__invite-wrapper">
                    <button
                      type="button"
                      className="um-icon-btn"
                      aria-label="Resend invite"
                      onClick={() => actions.onInvite?.(user)}
                      disabled={readOnly || actions.inviteLoading}
                    >
                      <Iconography
                        icon="envelop"
                        size={IconographySizes.Small}
                      />
                    </button>
                    {actions.inviteLoading && (
                      <Loader variant={LoaderSizes.small} />
                    )}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="um-icon-btn"
                aria-label="Edit user"
                onClick={() => actions.onEdit(user)}
                disabled={readOnly}
              >
                <Iconography icon="pen" size={IconographySizes.Small} />
              </button>
              {actions.onDelete && (
                <button
                  type="button"
                  className="um-icon-btn"
                  aria-label="Delete user"
                  onClick={() => actions.onDelete?.(user)}
                  disabled={readOnly}
                >
                  <Iconography icon="trashCan" size={IconographySizes.Small} />
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default UserManagementList;
